import { app } from '@/main'
import camelcaseKeys from 'camelcase-keys'
import productService from '@/services/products'
import receiptService from '@/services/receipts'
import store from '@/store'

export default {
  namespaced: true,
  state: {
    receipt: {
      receiptProducts: []
    }
  },

  getters: {
    products: (state) => {
      return state.receipt ? state.receipt.receiptProducts : []
    },

    productExistsInReceipt: (state) => (productId) => {
      return state.receipt ? state.receipt.receiptProducts.some(product => product.productId === productId) : false
    },

    itemCount: (state) => {
      return state.receipt.receiptProducts.reduce(function (accumulator, receiptProduct) {
        return accumulator + Math.abs(receiptProduct.qty)
      }, 0)
    },
    refundAmount: (state) => {
      if (state.receipt.transactions) {
        return state.receipt.transactions
          .filter((transaction) => transaction.totalInclVat < 0)
          .reduce(function (accumulator, transaction) {
            return accumulator - Math.abs(transaction.totalInclVat)
          }, 0)
      }
    },
    isOnlyRefund: (state) => {
      if (state.receipt.transactions) {
        return state.receipt.transactions
          .filter((transaction) => transaction.totalInclVat < 0)
          .length
      }
    }
  },

  actions: {
    ADD_NOTE ({ dispatch }, { receiptUuid, note }) {
      return receiptService.addNote({ receiptUuid, note })
        .then((data) => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    ADD_PRODUCT ({ dispatch }, data) {
      // push product to database
      return receiptService.addProduct(data)
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => {
          // don't return on purpose
          dispatch('FETCH_RECEIPT').catch(data => console.log(data))
        })
    },

    ADD_SERVICE_ROW ({ dispatch }, data) {
      return receiptService.addServiceRow(data)
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    ADD_RECEIPT_DISCOUNT ({ dispatch }, { discountType, discountValue }) {
      return receiptService.addReceiptDiscount({ discountType, discountValue })
        .then((data) => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    ADD_RECEIPT_DISCOUNT_STAFF ({ dispatch }) {
      return receiptService.addReceiptDiscountStaff()
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    ADD_ROW_DISCOUNT ({ dispatch }, { receiptProductUuid, discountType, discountValue }) {
      return receiptService.addRowDiscount({ receiptProductUuid, discountType, discountValue })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    ADD_ROW_DISCOUNT_STAFF ({ dispatch }, { receiptProductUuid }) {
      return receiptService.addRowDiscountStaff({ receiptProductUuid })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    ADD_TRANSACTION ({ dispatch }, { paymentMethodId, amount, payload = null }) {
      return receiptService.addTransaction({ paymentMethodId, amount, payload })
        .then((data) => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    CANCEL_RECEIPT ({ dispatch }, receiptUuid) {
      return receiptService.cancel(receiptUuid)
        .then((data) => Promise.resolve(data.data))
        .catch((data) => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    CHANGE_CUSTOMER ({ dispatch }, { receiptUuid = null, customerUuid }) {
      app.$wait.start('changing customer')
      return receiptService.setCustomer(receiptUuid, customerUuid)
        .then(data => {
          dispatch('FETCH_RECEIPT').catch(data => console.log(data))
          return Promise.resolve(data.data)
        })
        .catch(data => Promise.reject(data.data))
        .finally(() => app.$wait.end('changing customer'))
    },

    CHANGE_LANGUAGE ({ dispatch }, language) {
      return receiptService.setLanguage(language)
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    CHANGE_PRODUCT_PRICE ({ dispatch }, { receiptProductUuid, price }) {
      return receiptService.changeProductPrice({ receiptProductUuid, price })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    CHANGE_PRODUCT_QUANTITY ({ dispatch }, { receiptProductUuid, qty }) {
      // push product to database
      return receiptService.changeProductQuantity({ receiptProductUuid, qty })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    CHANGE_PRODUCT_NOTE ({ dispatch }, { receiptProductUuid, note }) {
      return receiptService.changeProductNote({ receiptProductUuid, note })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    CHANGE_SAMPLE_QUANTITY ({ dispatch }, { receiptProductUuid, qty }) {
      return receiptService.changeSampleQuantity({ receiptProductUuid, qty })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    CHECK_REFUND_NEEDED ({ dispatch }) {
      return receiptService.checkRefundNeeded()
        .then(data => {
          return dispatch('FETCH_RECEIPT').catch(data => console.log(data))
            .then(() => Promise.resolve(data.data))
        })
        .catch(data => Promise.reject(data.data))
    },

    CLEAR_PRODUCTS ({ dispatch }) {
      return receiptService.removeProducts()
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    COMPLETE_RECEIPT ({ dispatch }) {
      return receiptService.complete()
        .then((data) => {
          dispatch('RESET_RECEIPT')
          // redirect to login page
          dispatch('AUTH/LOGOUT', store.getters.token, { root: true })
          return Promise.resolve(data.data)
        })
        .catch(data => Promise.reject(data.data))
    },

    FETCH_RECEIPT ({ commit, dispatch }) {
      app.$wait.start('fetching receipt')
      return receiptService.get()
        .then(data => {
          app.$wait.end('fetching receipt')
          // store.dispatch('CANCEL_PENDING_REQUESTS')
          if (data.data.receipt) {
            commit('SET_RECEIPT', data.data.receipt)
            commit('SET_PRODUCTS', data.data.receipt.receipt_products)
            return Promise.resolve(data.data)
          } else { // receipt is yet to be initialized, so empty cart
            return dispatch('RESET_RECEIPT')
          }
        })
        .catch((data) => {
          // Don't show swal popup when we cancel fetch_receipt manually
          if (data.message === 'CANCEL') {
            return Promise.resolve('CANCEL')
          }

          app.$swal({
            title: app.$t('Warning'),
            text: `${app.$t('{0} couldn\'t be loaded', [app.$t('Receipt')])}`,
            type: 'warning',
            confirmButtonText: app.$t('Reload'),
            allowOutsideClick: false
          })
            .then((result) => {
              if (result.value) {
                // Trigger page refresh
                location.reload()
              }
            })

          // Not in finally block, if we cancel a fetch_receipt call the spinner should keep spinning
          // There is a subsequent fetch_receipt call coming
          app.$wait.end('fetching receipt')
          return Promise.reject(data.data)
        })
    },

    IMPORT_RECEIPT ({ dispatch }, receiptUuid) {
      return receiptService.import(receiptUuid)
        .then((data) => Promise.resolve(data.data))
        .catch((data) => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    PARK_RECEIPT ({ dispatch }, name) {
      return receiptService.park(name)
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    REMOVE_DISCOUNTS ({ dispatch }) {
      return receiptService.addReceiptDiscount({ discountType: null, discountValue: null })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    REMOVE_NOTE ({ dispatch }, { receiptUuid }) {
      return receiptService.addNote({ receiptUuid, note: null })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    REMOVE_PRODUCT ({ dispatch }, id) {
      return receiptService.removeProduct(id)
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    REMOVE_RECEIPT_DISCOUNT ({ dispatch }) {
      return receiptService.addReceiptDiscount({ discountType: null, discountValue: null })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    REMOVE_ROW_DISCOUNT ({ dispatch }, { receiptProductUuid }) {
      return receiptService.addRowDiscount({ receiptProductUuid, discountType: null, discountValue: null, discountField: null })
        .then(data => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    REMOVE_TRANSACTION ({ dispatch }, transactionUuid) {
      return receiptService.removeTransaction(transactionUuid)
        .then((data) => Promise.resolve(data.data))
        .catch(data => Promise.reject(data.data))
        .finally(() => dispatch('FETCH_RECEIPT').catch(data => console.log(data)))
    },

    RESET_RECEIPT ({ commit }) {
      commit('SET_RECEIPT', { receiptProducts: [] })
      commit('SET_PRODUCTS', [])
    },

    SCAN_PRODUCT ({ dispatch }, barcode) {
      // fetch product by barcode
      return productService.findByBarcode(barcode)
        .then(data => dispatch('ADD_PRODUCT', { id: data.data.product.id, qty: 1 }).then(data => Promise.resolve(data)))
        .catch(data => Promise.reject(data.data))
    }
  },
  mutations: {
    SET_CUSTOMER (state, customer) {
      customer = customer ? camelcaseKeys(customer, { deep: true }) : customer
      state.customer = customer
    },

    SET_PRODUCTS (state, products) {
      products = products ? camelcaseKeys(products, { deep: true }) : products
      state.receipt.receiptProducts = products
    },

    SET_RECEIPT (state, receipt) {
      receipt = receipt ? camelcaseKeys(receipt, { deep: true }) : receipt
      state.receipt = receipt
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/receipt.js